import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Calendar as CalendarIcon, Clock, CheckCircle2, Loader2, Video } from 'lucide-react';
import ReactCalendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import '../calendar-custom.css';
import axios from 'axios';
import moment from 'moment-timezone';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BookingModal = ({ isOpen, onClose }) => {
  const [step, setStep] = useState(1); // 1: Date, 2: Time, 3: Details, 4: Confirmation
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [bookingDetails, setBookingDetails] = useState({
    name: '',
    email: '',
    notes: ''
  });
  const [confirmation, setConfirmation] = useState(null);
  const [userTimezone, setUserTimezone] = useState('UTC');

  useEffect(() => {
    // Detect user's timezone
    const timezone = moment.tz.guess();
    setUserTimezone(timezone);
  }, []);

  useEffect(() => {
    if (selectedDate && step === 2) {
      fetchAvailableSlots();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  const fetchAvailableSlots = async () => {
    setLoading(true);
    try {
      // Use the user's timezone for start/end of day
      const startOfDay = moment.tz(selectedDate, userTimezone).startOf('day');
      const endOfDay = moment.tz(selectedDate, userTimezone).endOf('day');

      const response = await axios.post(`${API}/booking/available-slots`, {
        start_date: startOfDay.format('YYYY-MM-DDTHH:mm:ss'),
        end_date: endOfDay.format('YYYY-MM-DDTHH:mm:ss'),
        timezone: userTimezone
      });

      setAvailableSlots(response.data.slots || []);
    } catch (error) {
      console.error('Error fetching slots:', error);
      if (error.response?.status === 401) {
        alert('Booking system is not configured. Admin needs to authorize Google Calendar.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setStep(2);
  };

  const handleSlotSelect = (slot) => {
    setSelectedSlot(slot);
    setStep(3);
  };

  const handleSubmitBooking = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/booking/create`, {
        client_name: bookingDetails.name,
        client_email: bookingDetails.email,
        start_datetime: selectedSlot.start_iso,
        timezone: userTimezone,
        notes: bookingDetails.notes
      });

      setConfirmation(response.data);
      setStep(4);
    } catch (error) {
      console.error('Error creating booking:', error);
      alert('Failed to create booking. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setSelectedDate(null);
    setSelectedSlot(null);
    setBookingDetails({ name: '', email: '', notes: '' });
    setConfirmation(null);
    onClose();
  };

  const isWeekday = (date) => {
    const day = date.getDay();
    return day !== 0 && day !== 6; // Not Sunday (0) or Saturday (6)
  };

  const formatTime = (isoString) => {
    return moment(isoString).tz(userTimezone).format('h:mm A');
  };

  const formatDate = (isoString) => {
    return moment(isoString).tz(userTimezone).format('MMMM D, YYYY');
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">
            {step === 4 ? 'Booking Confirmed!' : 'Book a Call'}
          </DialogTitle>
          <DialogDescription>
            {step === 1 && 'Select a date for your consultation call'}
            {step === 2 && 'Choose an available time slot'}
            {step === 3 && 'Enter your details'}
            {step === 4 && 'Your meeting has been scheduled'}
          </DialogDescription>
        </DialogHeader>

        {/* Step 1: Date Selection */}
        {step === 1 && (
          <div className="py-4">
            <div className="flex justify-center">
              <ReactCalendar
                onChange={handleDateSelect}
                value={selectedDate}
                minDate={new Date()}
                tileDisabled={({ date }) => !isWeekday(date)}
                className="border rounded-lg p-4"
              />
            </div>
            <p className="text-sm text-gray-500 mt-4 text-center">
              Available: Monday - Friday
            </p>
            <p className="text-sm text-gray-500 text-center">
              Your timezone: {userTimezone}
            </p>
          </div>
        )}

        {/* Step 2: Time Selection */}
        {step === 2 && (
          <div className="py-4">
            <div className="mb-4">
              <Button variant="ghost" onClick={() => setStep(1)}>
                ← Change Date
              </Button>
              <p className="text-lg font-medium mt-2">
                {moment(selectedDate).format('MMMM D, YYYY')}
              </p>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin" />
              </div>
            ) : availableSlots.length > 0 ? (
              <div className="grid grid-cols-3 gap-3 max-h-96 overflow-y-auto">
                {availableSlots.map((slot, index) => (
                  <Button
                    key={index}
                    variant="outline"
                    className="h-auto py-3"
                    onClick={() => handleSlotSelect(slot)}
                  >
                    <Clock className="h-4 w-4 mr-2" />
                    {formatTime(slot.start_iso)}
                  </Button>
                ))}
              </div>
            ) : (
              <p className="text-center text-gray-500 py-8">
                No available slots for this date. Please select another date.
              </p>
            )}
          </div>
        )}

        {/* Step 3: Details Form */}
        {step === 3 && (
          <form onSubmit={handleSubmitBooking} className="py-4 space-y-4">
            <div className="bg-gray-50 p-4 rounded-lg mb-4">
              <p className="text-sm font-medium">Selected Time:</p>
              <p className="text-lg font-bold">
                {formatDate(selectedSlot.start_iso)} at {formatTime(selectedSlot.start_iso)}
              </p>
              <p className="text-sm text-gray-600">Duration: 30 minutes</p>
            </div>

            <Button
              type="button"
              variant="ghost"
              onClick={() => setStep(2)}
              className="mb-4"
            >
              ← Change Time
            </Button>

            <div>
              <Label htmlFor="name">Full Name *</Label>
              <Input
                id="name"
                value={bookingDetails.name}
                onChange={(e) => setBookingDetails({ ...bookingDetails, name: e.target.value })}
                required
                placeholder="John Doe"
              />
            </div>

            <div>
              <Label htmlFor="email">Email Address *</Label>
              <Input
                id="email"
                type="email"
                value={bookingDetails.email}
                onChange={(e) => setBookingDetails({ ...bookingDetails, email: e.target.value })}
                required
                placeholder="john@example.com"
              />
            </div>

            <div>
              <Label htmlFor="notes">Additional Notes (Optional)</Label>
              <Textarea
                id="notes"
                value={bookingDetails.notes}
                onChange={(e) => setBookingDetails({ ...bookingDetails, notes: e.target.value })}
                placeholder="Tell us about your project or what you'd like to discuss..."
                rows={4}
              />
            </div>

            <Button
              type="submit"
              className="w-full bg-black hover:bg-gray-800 text-white"
              size="lg"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Booking...
                </>
              ) : (
                'Confirm Booking'
              )}
            </Button>
          </form>
        )}

        {/* Step 4: Confirmation */}
        {step === 4 && confirmation && (
          <div className="py-6 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
              <CheckCircle2 className="h-8 w-8 text-green-600" />
            </div>
            <h3 className="text-xl font-bold mb-2">Booking Confirmed!</h3>
            <p className="text-gray-600 mb-6">
              Your consultation call has been scheduled.
            </p>

            <div className="bg-gray-50 p-6 rounded-lg text-left mb-6">
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <CalendarIcon className="h-5 w-5 text-gray-600 mt-0.5" />
                  <div>
                    <p className="font-medium">Date & Time</p>
                    <p className="text-gray-600">
                      {formatDate(selectedSlot.start_iso)} at {formatTime(selectedSlot.start_iso)}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Video className="h-5 w-5 text-gray-600 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-medium">Google Meet Link</p>
                    <a
                      href={confirmation.meet_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline break-all"
                    >
                      {confirmation.meet_link}
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <p className="text-sm text-gray-600 mb-6">
              A calendar invitation with the Google Meet link has been sent to your email.
            </p>

            <Button
              onClick={handleClose}
              className="bg-black hover:bg-gray-800 text-white"
              size="lg"
            >
              Done
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BookingModal;